import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import LogoCircuit from "../logos/LogoCircuit";

// Types for auction data
interface AuctionItem {
  id: number;
  domain: string;
  currentBid: number;
  nextBid: number;
  bidders: number;
  bids: number;
  highBidder: string;
  endTime: string;
  timeRemaining: string;
  recordType: string;
}

interface RawAuctionItem {
  id: number;
  name: string;
  numberOfBidders: number;
  highBid: number;
  nextValidBid: number;
  highBidder: string;
  highBidderAlias: string;
  numberOfBids: number;
  expirationDate: string;
  recordType: string;
}

interface LandingData {
  success: boolean;
  topByBid: RawAuctionItem[];
  expiringSoon: RawAuctionItem[];
  stats: {
    totalDomains: number;
    auctionsWithBids: number;
  };
  topTlds: { tld: string; count: number }[];
  statusCode: string;
}

// Helper to transform raw auction to display format
function transformAuction(raw: RawAuctionItem): AuctionItem {
  const getTimeRemaining = (endTime: string): string => {
    const end = new Date(endTime).getTime();
    const now = Date.now();
    const diff = end - now;
    if (diff <= 0) return "ENDED";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}D ${hours % 24}H`;
    }
    return `${hours}H ${minutes}M`;
  };

  return {
    id: raw.id,
    domain: raw.name,
    currentBid: raw.highBid,
    nextBid: raw.nextValidBid,
    bidders: raw.numberOfBidders,
    bids: raw.numberOfBids,
    highBidder: raw.highBidderAlias || `#${raw.highBidder}` || "Unknown",
    endTime: raw.expirationDate,
    timeRemaining: getTimeRemaining(raw.expirationDate),
    recordType: raw.recordType,
  };
}

// Domain Detail Modal Component
const DomainModal: React.FC<{
  auction: AuctionItem | null;
  onClose: () => void;
  landingData?: LandingData | null;
}> = ({ auction, onClose, landingData }) => {
  if (!auction) return null;

  const getTld = (domain: string) => {
    const parts = domain.split(".");
    return parts.length > 1 ? `.${parts[parts.length - 1]}` : "";
  };

  const tld = getTld(auction.domain);

  // Get TLD count from landing data
  const tldCount = landingData?.topTlds?.find(t => t.tld === tld)?.count || 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="border-2 border-green-500 bg-black p-6 max-w-lg w-full"
          style={{ boxShadow: "0 0 50px rgba(34, 197, 94, 0.3)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4 border-b border-green-700 pb-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-green-500 text-xs tracking-wider">AUCTION DETAILS</span>
            </div>
            <button onClick={onClose} className="text-green-600 hover:text-green-400 text-xl">
              [X]
            </button>
          </div>

          <h2 className="text-2xl font-bold text-green-400 mb-4">{auction.domain}</h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="border border-green-700 p-3">
              <div className="text-green-600 text-xs">CURRENT BID</div>
              <div className="text-green-400 text-xl font-bold">${auction.currentBid.toLocaleString()}</div>
            </div>
            <div className="border border-green-700 p-3">
              <div className="text-green-600 text-xs">NEXT BID</div>
              <div className="text-green-400 text-xl font-bold">${auction.nextBid.toLocaleString()}</div>
            </div>
            <div className="border border-green-700 p-3">
              <div className="text-green-600 text-xs">BIDDERS</div>
              <div className="text-green-400 text-xl font-bold">{auction.bidders}</div>
            </div>
            <div className="border border-green-700 p-3">
              <div className="text-green-600 text-xs">TOTAL BIDS</div>
              <div className="text-green-400 text-xl font-bold">{auction.bids}</div>
            </div>
            <div className="border border-green-700 p-3">
              <div className="text-green-600 text-xs">TIME LEFT</div>
              <div className="text-yellow-500 text-xl font-bold">{auction.timeRemaining}</div>
            </div>
            <div className="border border-green-700 p-3">
              <div className="text-green-600 text-xs">TYPE</div>
              <div className="text-green-400 text-xl font-bold">{auction.recordType}</div>
            </div>
          </div>

          <div className="border border-green-700 p-3 mb-6">
            <div className="text-green-600 text-xs mb-1">HIGH BIDDER</div>
            <div className="text-green-400 font-mono">{auction.highBidder}</div>
          </div>

          {tldCount > 0 && (
            <div className="border border-green-700 p-3 mb-6">
              <div className="text-green-600 text-xs mb-1">{tld.toUpperCase()} DOMAINS IN FEED</div>
              <div className="text-green-400 font-bold">{tldCount.toLocaleString()}</div>
            </div>
          )}

          <a
            href={`https://www.dropcatch.com/domain/${auction.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-6 py-4 bg-green-500 text-black font-bold hover:bg-green-400 transition-all tracking-wider"
            style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.5)" }}
          >
            [VIEW ON DROPCATCH]
          </a>

          <div className="mt-4 text-center text-green-700 text-xs">
            Opens dropcatch.com in a new tab
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hero Live Terminal Component
const HeroLiveTerminal: React.FC<{
  landingData?: LandingData | null;
  onAuctionClick: (auction: AuctionItem) => void;
  error?: string | null;
}> = ({ landingData, onAuctionClick, error }) => {
  const [currentTime, setCurrentTime] = useState(new Date().toISOString());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().toISOString());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get total domains from stats
  const totalDomains = landingData?.stats?.totalDomains || 0;

  // Get top auctions by bid (only 3) - already sorted by API
  const topAuctions = landingData?.topByBid?.slice(0, 3).map(transformAuction) || [];

  // Get domains expiring soonest (only 3) - already sorted by API
  const expiringNow = landingData?.expiringSoon?.slice(0, 3).map(transformAuction) || [];

  // Get TLD counts from API
  const getTldCount = (tld: string) => {
    const tldData = landingData?.topTlds?.find((t: { tld: string; count: number }) => t.tld === tld);
    return tldData?.count || 0;
  };

  const comCount = getTldCount(".com");
  const netCount = getTldCount(".net");
  const orgCount = getTldCount(".org");

  // Get max bid from top auctions
  const bids = landingData?.topByBid?.map((item: RawAuctionItem) => item.highBid).filter((b: number) => b > 0) || [];
  const maxBid = bids.length > 0 ? Math.max(...bids) : 0;

  return (
    <div>
      {/* Terminal Header */}
      <div
        className="border-2 border-green-500 bg-black max-h-[480px] overflow-hidden"
        style={{ boxShadow: "0 0 30px rgba(34, 197, 94, 0.3)" }}
      >
        <div className="flex items-center gap-2 px-4 py-2 border-b border-green-700 bg-green-950/30">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          <span className="text-green-500 text-xs tracking-wider ml-2">
            DROPCATCH LIVE FEED
          </span>
          <div className="ml-auto flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-green-600 text-xs font-mono">
              {landingData ? "CONNECTED" : "CONNECTING..."}
            </span>
          </div>
        </div>

        {/* Timestamp & Total */}
        <div className="px-4 py-2 border-b border-green-900 bg-black flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="text-green-500 text-xs font-mono">
              {currentTime.split("T")[1]?.slice(0, 8) || "00:00:00"}
            </span>
            <span className="text-green-400 text-sm font-bold">
              {totalDomains > 0 ? `${(totalDomains / 1000).toFixed(0)}K` : "---"} AUCTIONS
            </span>
          </div>
          <span className="text-green-500 text-xs">
            MAX: ${maxBid > 0 ? maxBid.toLocaleString() : "---"}
          </span>
        </div>

        {/* TLD Filters with counts */}
        <div className="grid grid-cols-3 border-b border-green-900">
          <div className="p-2 border-r border-green-900 text-center">
            <div className="text-green-400 text-lg font-bold">.com</div>
            <div className="text-green-600 text-xs font-mono">
              ({comCount > 0 ? comCount.toLocaleString() : "---"})
            </div>
          </div>
          <div className="p-2 border-r border-green-900 text-center">
            <div className="text-green-400 text-lg font-bold">.net</div>
            <div className="text-green-600 text-xs font-mono">
              ({netCount > 0 ? netCount.toLocaleString() : "---"})
            </div>
          </div>
          <div className="p-2 text-center">
            <div className="text-green-400 text-lg font-bold">.org</div>
            <div className="text-green-600 text-xs font-mono">
              ({orgCount > 0 ? orgCount.toLocaleString() : "---"})
            </div>
          </div>
        </div>

        {/* Top Auctions */}
        <div className="p-2 border-b border-green-900">
          <div className="text-green-600 text-[10px] tracking-wider mb-1 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            TOP AUCTIONS BY BID
          </div>
          <div className="space-y-1">
            {topAuctions.length > 0 ? (
              topAuctions.map((auction, i) => (
                <button
                  key={auction.id}
                  onClick={() => onAuctionClick(auction)}
                  className="w-full flex items-center justify-between py-1 px-2 hover:bg-green-950/50 transition-colors border border-transparent hover:border-green-700 group"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-green-700 text-xs w-4">{i + 1}.</span>
                    <span className="text-green-400 text-sm font-bold group-hover:text-green-300 truncate max-w-[120px]">
                      {auction.domain}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500 text-sm font-bold">
                      ${auction.currentBid.toLocaleString()}
                    </span>
                    <span className="text-green-700 text-[10px]">
                      {auction.bidders}b
                    </span>
                  </div>
                </button>
              ))
            ) : error ? (
              <div className="text-red-500 text-xs py-2 text-center">{error}</div>
            ) : (
              <div className="text-green-700 text-xs py-2 text-center">Loading...</div>
            )}
          </div>
        </div>

        {/* Expiring Now */}
        <div className="p-2">
          <div className="text-yellow-600 text-[10px] tracking-wider mb-1 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse" />
            EXPIRING NOW
          </div>
          <div className="space-y-1">
            {expiringNow.length > 0 ? (
              expiringNow.map((auction) => (
                <button
                  key={`exp-${auction.id}`}
                  onClick={() => onAuctionClick(auction)}
                  className="w-full flex items-center justify-between py-1 px-2 hover:bg-yellow-950/30 transition-colors border border-transparent hover:border-yellow-700 group"
                >
                  <span className="text-green-400 text-sm font-bold group-hover:text-yellow-300 truncate max-w-[140px]">
                    {auction.domain}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 text-xs">
                      ${auction.currentBid.toLocaleString()}
                    </span>
                    <span className="text-yellow-500 text-xs font-bold">
                      {auction.timeRemaining}
                    </span>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-green-700 text-xs py-2 text-center">Loading...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Feature Preview Component - Shows actual UI
const FeaturePreview: React.FC<{
  feature: {
    id: string;
    title: string;
    description: string;
    preview: React.ReactNode;
  };
  isActive: boolean;
}> = ({ feature, isActive }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isActive ? 1 : 0.5, y: 0 }}
      className={`border-2 ${isActive ? "border-green-500" : "border-green-800"} bg-black p-4 transition-all`}
      style={isActive ? { boxShadow: "0 0 30px rgba(34, 197, 94, 0.2)" } : {}}
    >
      <div className="flex items-center gap-2 mb-3 border-b border-green-700 pb-2">
        <div className={`w-2 h-2 ${isActive ? "bg-green-500 animate-pulse" : "bg-green-800"} rounded-full`} />
        <span className="text-green-500 text-xs tracking-wider">{feature.title}</span>
      </div>
      <p className="text-green-600 text-xs mb-3">{feature.description}</p>
      <div className="border border-green-800 bg-black/50 p-3 min-h-[100px]">
        {feature.preview}
      </div>
    </motion.div>
  );
};

// Features Showcase Section - F1-F10 Dashboard Navigation
const FeaturesShowcase: React.FC<{
  landingData?: LandingData | null;
}> = ({ landingData }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeFeature, setActiveFeature] = useState(0);

  // Features matching dashboard F1-F10 navigation
  const features = [
    {
      id: "dashboard",
      key: "F1",
      title: "DASHBOARD",
      description: "Central command center. Real-time stats, quick actions, and market overview at a glance.",
      preview: (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="border border-green-700 p-2 text-center">
              <div className="text-green-500 text-lg font-bold">{landingData?.stats?.totalDomains?.toLocaleString() || "755K"}</div>
              <div className="text-green-700 text-[9px]">DOMAINS</div>
            </div>
            <div className="border border-green-700 p-2 text-center">
              <div className="text-green-500 text-lg font-bold">12</div>
              <div className="text-green-700 text-[9px]">ACTIVE BIDS</div>
            </div>
            <div className="border border-green-700 p-2 text-center">
              <div className="text-yellow-500 text-lg font-bold">3</div>
              <div className="text-green-700 text-[9px]">ALERTS</div>
            </div>
          </div>
          <div className="border border-green-800 p-2">
            <div className="text-green-600 text-[10px] mb-1">&gt; QUICK ACTIONS</div>
            <div className="flex gap-2">
              <span className="border border-green-600 px-2 py-1 text-green-500 text-[10px]">[NEW BID]</span>
              <span className="border border-green-600 px-2 py-1 text-green-500 text-[10px]">[SEARCH]</span>
              <span className="border border-green-600 px-2 py-1 text-green-500 text-[10px]">[AI]</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "search",
      key: "F2",
      title: "SEARCH",
      description: `Search ${landingData?.stats?.totalDomains?.toLocaleString() || "755,000+"} live auctions with advanced filters.`,
      preview: (
        <div className="space-y-2">
          <div className="flex gap-1">
            <input
              type="text"
              placeholder="Search domains..."
              className="flex-1 bg-black border border-green-700 px-2 py-1 text-green-400 text-xs placeholder-green-800"
              disabled
            />
            <button className="border border-green-500 px-2 text-green-500 text-xs">[GO]</button>
          </div>
          <div className="flex gap-1 flex-wrap">
            {[".com", ".net", ".io", "< $500", "4-6 chars"].map((f) => (
              <span key={f} className="border border-green-800 px-1 text-[10px] text-green-600">{f}</span>
            ))}
          </div>
          <div className="border-t border-green-900 pt-2 space-y-1">
            {(landingData?.topByBid?.slice(0, 2) || [{ name: "tech.com", highBid: 450 }, { name: "startup.io", highBid: 230 }]).map((a: any, i: number) => (
              <div key={i} className="flex justify-between text-[10px]">
                <span className="text-green-400">{a.name}</span>
                <span className="text-green-500">${a.highBid?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: "backorders",
      key: "F3",
      title: "BACKORDERS",
      description: "Place backorders on dropping domains. Get notified when auctions start.",
      preview: (
        <div className="space-y-2">
          <div className="border border-yellow-700 p-2 bg-yellow-950/20">
            <div className="flex justify-between items-center">
              <span className="text-yellow-500 text-xs font-bold">premium.com</span>
              <span className="text-yellow-500 text-[10px] border border-yellow-600 px-1">PENDING</span>
            </div>
            <div className="text-green-600 text-[10px] mt-1">Max Bid: $1,500 | Drops: 2D 14H</div>
          </div>
          <div className="border border-green-700 p-2">
            <div className="flex justify-between items-center">
              <span className="text-green-400 text-xs font-bold">startup.io</span>
              <span className="text-green-500 text-[10px] border border-green-600 px-1">ACTIVE</span>
            </div>
            <div className="text-green-600 text-[10px] mt-1">Max Bid: $800 | Ends: 5H 23M</div>
          </div>
        </div>
      ),
    },
    {
      id: "bids",
      key: "F4",
      title: "MY BIDS",
      description: "Track all active bids. Real-time status updates and outbid alerts.",
      preview: (
        <div className="space-y-2">
          <div className="flex justify-between items-center border border-green-500 p-2 bg-green-950/20">
            <div>
              <span className="text-green-400 text-xs">tech.ai</span>
              <span className="text-green-600 text-[10px] ml-2">$450</span>
            </div>
            <span className="text-black text-[10px] bg-green-500 px-1 font-bold">WINNING</span>
          </div>
          <div className="flex justify-between items-center border border-red-700 p-2 bg-red-950/20">
            <div>
              <span className="text-green-400 text-xs">brand.co</span>
              <span className="text-green-600 text-[10px] ml-2">$230</span>
            </div>
            <span className="text-red-500 text-[10px] border border-red-500 px-1">OUTBID</span>
          </div>
          <div className="text-green-700 text-[10px] pt-1 border-t border-green-900">
            Active: 12 | Winning: 8 | Outbid: 4
          </div>
        </div>
      ),
    },
    {
      id: "watchlist",
      key: "F5",
      title: "WATCHLIST",
      description: "Monitor domains without bidding. Get alerts on price changes and ending soon.",
      preview: (
        <div className="space-y-2">
          <div className="text-yellow-500 text-[10px] animate-pulse flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
            2 AUCTIONS ENDING SOON
          </div>
          {(landingData?.expiringSoon?.slice(0, 3) || [
            { name: "crypto.net", highBid: 890 },
            { name: "app.io", highBid: 450 },
            { name: "tech.co", highBid: 230 }
          ]).map((a: any, i: number) => (
            <div key={i} className="flex justify-between text-xs border border-green-900 p-1">
              <span className="text-green-400">{a.name}</span>
              <span className="text-green-500">${a.highBid?.toLocaleString()}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: "portfolio",
      key: "F6",
      title: "PORTFOLIO",
      description: "Manage your won domains. Track values, renewals, and sales.",
      preview: (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="border border-green-700 p-2">
              <div className="text-green-500 text-sm font-bold">23</div>
              <div className="text-green-700 text-[9px]">DOMAINS OWNED</div>
            </div>
            <div className="border border-green-700 p-2">
              <div className="text-green-500 text-sm font-bold">$45K</div>
              <div className="text-green-700 text-[9px]">TOTAL VALUE</div>
            </div>
          </div>
          <div className="border border-green-800 p-2 space-y-1">
            <div className="flex justify-between text-[10px]">
              <span className="text-green-400">startup.io</span>
              <span className="text-green-500">$8,500</span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span className="text-green-400">tech.ai</span>
              <span className="text-green-500">$12,000</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "analytics",
      key: "F7",
      title: "ANALYTICS",
      description: "Track bidding performance, win rates, and spending over time.",
      preview: (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="border border-green-700 p-1">
              <div className="text-green-500 text-sm font-bold">67%</div>
              <div className="text-green-700 text-[8px]">WIN RATE</div>
            </div>
            <div className="border border-green-700 p-1">
              <div className="text-green-500 text-sm font-bold">$4.2K</div>
              <div className="text-green-700 text-[8px]">SPENT</div>
            </div>
            <div className="border border-green-700 p-1">
              <div className="text-green-500 text-sm font-bold">$287</div>
              <div className="text-green-700 text-[8px]">AVG BID</div>
            </div>
          </div>
          <div className="border border-green-800 p-2">
            <div className="text-green-600 text-[10px] mb-1">&gt; MONTHLY TREND</div>
            <div className="flex items-end gap-1 h-8">
              {[40, 55, 45, 70, 60, 85, 75].map((h, i) => (
                <div key={i} className="flex-1 bg-green-700" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "history",
      key: "F8",
      title: "HISTORY",
      description: "Complete auction history. All bids, wins, and losses with details.",
      preview: (
        <div className="space-y-1 text-[10px]">
          <div className="flex justify-between items-center text-green-400 border-b border-green-900 pb-1">
            <span>+ WON</span>
            <span>tech.ai</span>
            <span>$450</span>
          </div>
          <div className="flex justify-between items-center text-red-500 border-b border-green-900 pb-1">
            <span>- LOST</span>
            <span>brand.co</span>
            <span>$230</span>
          </div>
          <div className="flex justify-between items-center text-green-400 border-b border-green-900 pb-1">
            <span>+ WON</span>
            <span>app.io</span>
            <span>$180</span>
          </div>
          <div className="text-green-600 mt-2 pt-2 border-t border-green-800">
            Total: 156 auctions | Won: 104 | Lost: 52
          </div>
        </div>
      ),
    },
    {
      id: "ai-chat",
      key: "F9",
      title: "AI CHAT",
      description: "Google Gemini-powered analysis. Valuations, bidding strategies, market insights.",
      preview: (
        <div className="space-y-2 text-xs">
          <div className="text-green-600">&gt; analyze EcoLog.com</div>
          <div className="text-green-400 border-l-2 border-green-500 pl-2">
            <div className="font-bold mb-1 text-[10px]">DOMAIN ANALYSIS</div>
            <div className="text-[10px]">Est. Value: $8,000 - $15,000</div>
            <div className="text-[10px]">Current: ${landingData?.topByBid?.[0]?.highBid?.toLocaleString() || "4,000"}</div>
            <div className="text-yellow-500 text-[10px]">Recommendation: BID</div>
          </div>
        </div>
      ),
    },
    {
      id: "automations",
      key: "F10",
      title: "AUTOMATIONS",
      description: "Create bidding rules. Auto-bid on domains matching your criteria.",
      preview: (
        <div className="space-y-2">
          <div className="border border-green-700 p-2">
            <div className="flex justify-between items-center">
              <span className="text-green-400 text-xs font-bold">Tech Domains</span>
              <span className="text-green-500 text-[10px] px-1 border border-green-500">ACTIVE</span>
            </div>
            <div className="text-green-600 text-[10px] mt-1">IF .com AND len &lt; 8 AND bid &lt; $500</div>
            <div className="text-green-700 text-[10px]">Auto-bid: $750 max</div>
          </div>
          <div className="border border-green-700 p-2">
            <div className="flex justify-between items-center">
              <span className="text-green-400 text-xs font-bold">Premium .io</span>
              <span className="text-yellow-500 text-[10px] px-1 border border-yellow-500">PAUSED</span>
            </div>
            <div className="text-green-600 text-[10px] mt-1">IF .io AND no-numbers</div>
          </div>
        </div>
      ),
    },
  ];

  // Timer state for countdown
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<NodeJS.Timeout | null>(null);
  const TIMER_DURATION = 8000; // 8 seconds
  const PROGRESS_INTERVAL = 50; // Update progress every 50ms

  // Reset and start timer
  const startTimer = () => {
    // Clear existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);

    // Reset progress
    setProgress(0);
    let elapsed = 0;

    // Start progress updates
    progressRef.current = setInterval(() => {
      elapsed += PROGRESS_INTERVAL;
      setProgress((elapsed / TIMER_DURATION) * 100);
    }, PROGRESS_INTERVAL);

    // Set timer to advance feature
    timerRef.current = setTimeout(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, TIMER_DURATION);
  };

  // Handle manual selection - reset timer
  const handleFeatureSelect = (index: number) => {
    setActiveFeature(index);
    startTimer();
  };

  // Auto-rotate features with 8 second timer
  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [activeFeature]);

  return (
    <section ref={ref} className="py-20 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <div className="text-green-600 text-sm mb-2 tracking-widest">
            &gt; PLATFORM FEATURES
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-green-400 tracking-wider">
            COMMAND CENTER
          </h2>
          <p className="text-green-600 mt-4 max-w-2xl mx-auto">
            10 integrated tools for professional domain acquisition
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Feature Selector - F-Key Navigation */}
          <div className="space-y-1">
            {features.map((feature, i) => (
              <motion.button
                key={feature.id}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: i * 0.05 }}
                onClick={() => handleFeatureSelect(i)}
                className={`w-full p-2 border-2 text-left transition-all ${
                  activeFeature === i
                    ? "border-green-500 bg-green-950/30"
                    : "border-green-800 hover:border-green-600"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-mono px-1 border ${
                    activeFeature === i ? "border-green-500 text-green-400" : "border-green-700 text-green-600"
                  }`}>
                    [{feature.key}]
                  </span>
                  <span className={`text-xs font-bold tracking-wider ${
                    activeFeature === i ? "text-green-400" : "text-green-600"
                  }`}>
                    {feature.title}
                  </span>
                  {activeFeature === i && (
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse ml-auto" />
                  )}
                </div>
              </motion.button>
            ))}
          </div>

          {/* Feature Preview - Takes 2 columns */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <FeaturePreview feature={features[activeFeature]} isActive={true} />
              </motion.div>
            </AnimatePresence>

            {/* Progress Bar - 8 second countdown */}
            <div className="mt-4 h-1 bg-green-900 relative overflow-hidden">
              <div
                className="absolute h-full bg-green-500 transition-all duration-50 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 text-green-700 text-[10px]">
              <span>AUTO-ADVANCING</span>
              <span>{Math.ceil((100 - progress) / 100 * 8)}s</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section - Visual Terminal Components
const HowItWorks: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-20 px-6 border-y-2 border-green-500 bg-black">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-4"
        >
          <div className="text-green-600 text-sm mb-2 tracking-widest">
            &gt; PROFESSIONAL-GRADE DROPCATCH INTEGRATION
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-green-400 tracking-wider mb-4">
            HOW IT WORKS
          </h2>
          <p className="text-green-500 max-w-2xl mx-auto">
            Real-time auctions, automated backorders, and AI-powered analysis for serious domain investors.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {/* Step 1: CONNECT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="relative"
          >
            <div
              className="border-2 border-green-700 hover:border-green-500 bg-black h-full transition-all group"
              style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.1)" }}
            >
              <div className="border-b border-green-700 px-4 py-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-600 text-xs">01_CONNECT</span>
              </div>
              <div className="p-4">
                <div className="border border-green-800 bg-green-950/20 p-3 mb-4">
                  <div className="text-green-500 text-xs mb-2">&gt; API_CREDENTIALS</div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 border border-green-500 flex items-center justify-center text-green-500 text-xs">
                      [2FA]
                    </div>
                    <div className="flex-1 h-2 bg-green-900 overflow-hidden">
                      <div className="h-full bg-green-500 w-full animate-pulse" />
                    </div>
                  </div>
                  <div className="text-green-700 text-[10px]">ENCRYPTED | AES-256</div>
                </div>
                <h3 className="text-lg font-bold text-green-400 tracking-wider mb-2">
                  CONNECT
                </h3>
                <p className="text-green-500 text-sm mb-2">Link your DropCatch API securely</p>
                <p className="text-green-700 text-xs">
                  Credentials encrypted with 2FA. Use authenticator to decrypt. Control access duration.
                </p>
              </div>
            </div>
            <div className="hidden lg:block absolute top-1/2 -right-3 text-green-500 text-xl z-10">{">"}</div>
          </motion.div>

          {/* Step 2: DISCOVER */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div
              className="border-2 border-green-700 hover:border-green-500 bg-black h-full transition-all group"
              style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.1)" }}
            >
              <div className="border-b border-green-700 px-4 py-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-600 text-xs">02_DISCOVER</span>
              </div>
              <div className="p-4">
                <div className="border border-green-800 bg-green-950/20 p-3 mb-4">
                  <div className="flex gap-1 mb-2">
                    <div className="flex-1 border border-green-700 px-2 py-1 text-green-500 text-xs">
                      search_
                    </div>
                    <div className="border border-green-500 px-2 py-1 text-green-500 text-xs">[GO]</div>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {[".com", ".net", "$500", "4-6ch"].map((f) => (
                      <span key={f} className="border border-green-800 px-1 text-[10px] text-green-600">{f}</span>
                    ))}
                  </div>
                  <div className="text-green-400 text-[10px] mt-2">750K+ RESULTS</div>
                </div>
                <h3 className="text-lg font-bold text-green-400 tracking-wider mb-2">
                  DISCOVER
                </h3>
                <p className="text-green-500 text-sm mb-2">Search 750K+ domains in real-time</p>
                <p className="text-green-700 text-xs">
                  Filter by TLD, length, price range, auction type. Find opportunities others miss.
                </p>
              </div>
            </div>
            <div className="hidden lg:block absolute top-1/2 -right-3 text-green-500 text-xl z-10">{">"}</div>
          </motion.div>

          {/* Step 3: ANALYZE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <div
              className="border-2 border-green-700 hover:border-green-500 bg-black h-full transition-all group"
              style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.1)" }}
            >
              <div className="border-b border-green-700 px-4 py-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                <span className="text-green-600 text-xs">03_ANALYZE</span>
              </div>
              <div className="p-4">
                <div className="border border-green-800 bg-green-950/20 p-3 mb-4">
                  <div className="text-green-600 text-[10px] mb-1">&gt; GEMINI_AI</div>
                  <div className="text-green-400 text-xs font-bold">startup.io</div>
                  <div className="text-green-500 text-[10px]">Est: $5K-$12K</div>
                  <div className="text-yellow-500 text-[10px] mt-1">BID: 62% below market</div>
                  <div className="mt-2 h-1 bg-green-900 overflow-hidden">
                    <div className="h-full bg-yellow-500 w-3/4" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-green-400 tracking-wider mb-2">
                  ANALYZE
                </h3>
                <p className="text-green-500 text-sm mb-2">AI-powered domain evaluation</p>
                <p className="text-green-700 text-xs">
                  Instant valuations, market comparisons, bidding recommendations from Gemini AI.
                </p>
              </div>
            </div>
            <div className="hidden lg:block absolute top-1/2 -right-3 text-green-500 text-xl z-10">{">"}</div>
          </motion.div>

          {/* Step 4: ACQUIRE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            <div
              className="border-2 border-green-500 bg-black h-full transition-all group"
              style={{ boxShadow: "0 0 30px rgba(34, 197, 94, 0.2)" }}
            >
              <div className="border-b border-green-500 px-4 py-2 flex items-center gap-2 bg-green-950/30">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-500 text-xs">04_ACQUIRE</span>
              </div>
              <div className="p-4">
                <div className="border border-green-700 bg-green-950/20 p-3 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-green-400 text-xs font-bold">Auto-Bid Rule</span>
                    <span className="text-black text-[10px] bg-green-500 px-1">ACTIVE</span>
                  </div>
                  <div className="text-green-600 text-[10px]">IF .com AND bid {"<"} $500</div>
                  <div className="text-green-500 text-[10px]">THEN bid up to $750</div>
                  <div className="text-green-400 text-[10px] mt-2">3 domains acquired</div>
                </div>
                <h3 className="text-lg font-bold text-green-400 tracking-wider mb-2">
                  ACQUIRE
                </h3>
                <p className="text-green-500 text-sm mb-2">Bid or backorder with automation</p>
                <p className="text-green-700 text-xs">
                  Smart rules to auto-bid, place backorders, track everything from your dashboard.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Main Landing Page Component
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [showCursor, setShowCursor] = useState(true);
  const [landingData, setLandingData] = useState<LandingData | null>(null);
  const [selectedAuction, setSelectedAuction] = useState<AuctionItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch real-time auction data from DropCatch API (single combined endpoint)
  useEffect(() => {
    const fetchAuctionData = async () => {
      try {
        setError(null);

        const response = await fetch("/api/landing/realtimesearch");

        if (response.ok) {
          const data = await response.json();
          console.log("API data:", data);

          if (data.success) {
            setLandingData(data);
          }

          if (data.error) {
            console.error("API error:", data.error, data.details);
            setError(data.details || data.error);
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error("API failed:", response.status, errorData);
          setError(errorData.details || `API error: ${response.status}`);
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to connect to auction feed");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuctionData();
    const interval = setInterval(fetchAuctionData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="min-h-screen bg-black font-mono text-green-500 overflow-x-hidden"
      style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
    >
      {/* CRT Effects */}
      <div className="pointer-events-none fixed inset-0 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.1)_50%)] bg-[length:100%_4px] z-50" />
      <div className="pointer-events-none fixed inset-0 bg-green-500/[0.02] animate-pulse z-40" />
      <div
        className="pointer-events-none fixed inset-0 z-30"
        style={{
          background: "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%)",
        }}
      />

      {/* Domain Modal */}
      {selectedAuction && (
        <DomainModal
          auction={selectedAuction}
          onClose={() => setSelectedAuction(null)}
          landingData={landingData}
        />
      )}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-20 bg-black/90 backdrop-blur border-b-2 border-green-500">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <LogoCircuit size="md" animate={true} showText={true} />
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/login")}
              className="px-4 py-2 border border-green-700 text-green-500 hover:border-green-500 hover:bg-green-950/30 transition-all tracking-wider text-sm"
            >
              [LOGIN]
            </button>
            <button
              onClick={() => navigate("/signup")}
              className="px-4 py-2 bg-green-500 text-black font-bold hover:bg-green-400 transition-all tracking-wider text-sm"
              style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.5)" }}
            >
              [GET STARTED]
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Column 1 - Text */}
            <div>
              <div className="text-green-600 text-sm mb-4 tracking-widest">
                &gt; SYSTEM INITIALIZED
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-green-400 tracking-wider mb-6 leading-tight">
                DROPCATCH<br />
                <span className="text-green-500">INTELLIGENCE HUB</span>
              </h1>
              <h2 className="text-xl md:text-2xl text-green-600 tracking-wider mb-8">
                Professional-grade DropCatch integration for serious domain investors.
                Real-time auctions, automated backorders, and AI-powered analysis.
              </h2>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate("/signup")}
                  className="px-8 py-4 bg-green-500 text-black font-bold hover:bg-green-400 transition-all tracking-wider text-lg"
                  style={{ boxShadow: "0 0 30px rgba(34, 197, 94, 0.5)" }}
                >
                  [GET STARTED]
                </button>
                <button
                  onClick={() =>
                    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="px-8 py-4 border-2 border-green-500 text-green-500 hover:bg-green-950/30 transition-all tracking-wider text-lg"
                >
                  [LEARN MORE]
                </button>
              </div>
              <div className="mt-8 text-green-700 text-sm">
                <span className="text-green-500">$9.99</span> 3-day trial •{" "}
                <span className="text-green-500">$49.99/mo</span> full access
                <span className={showCursor ? "opacity-100" : "opacity-0"}> █</span>
              </div>
            </div>

            {/* Column 2 - Live Terminal */}
            <div className="relative">
              <HeroLiveTerminal
                landingData={landingData}
                onAuctionClick={setSelectedAuction}
                error={error}
              />
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 border border-green-700 opacity-30" />
              <div className="absolute -bottom-4 -left-4 w-16 h-16 border border-green-700 opacity-30" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 border-y-2 border-green-500 bg-green-950/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              {
                value: landingData?.stats?.totalDomains
                  ? `${(landingData.stats.totalDomains / 1000).toFixed(0)}K+`
                  : isLoading
                  ? "---"
                  : "750K+",
                label: "DOMAINS TRACKED",
                isLive: true,
              },
              {
                value: landingData?.stats?.auctionsWithBids
                  ? landingData.stats.auctionsWithBids.toLocaleString()
                  : isLoading
                  ? "---"
                  : "---",
                label: "WITH BIDS",
                isLive: true,
              },
              {
                value: landingData?.topByBid?.[0]?.highBid
                  ? `$${landingData.topByBid[0].highBid.toLocaleString()}`
                  : isLoading
                  ? "---"
                  : "$5,000+",
                label: "TOP BID",
                isLive: true,
              },
              {
                value: "99.9%",
                label: "UPTIME",
                isLive: false,
              },
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-bold text-green-400 flex items-center justify-center gap-2">
                  {stat.value}
                  {stat.isLive && !isLoading && landingData && (
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  )}
                </div>
                <div className="text-green-600 text-xs tracking-wider mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features">
        <FeaturesShowcase landingData={landingData} />
      </section>

      {/* How It Works */}
      <HowItWorks />

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-green-600 text-sm mb-2 tracking-widest">
              &gt; SYSTEM ACCESS LEVELS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-green-400 tracking-wider">
              PRICING
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Trial Package */}
            <div
              className="border-2 border-green-700 bg-black p-6"
              style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.1)" }}
            >
              <div className="text-green-600 text-xs tracking-wider mb-2">[TRIAL]</div>
              <div className="text-4xl font-bold text-green-400 mb-1">$9.99</div>
              <div className="text-green-600 text-sm mb-6">3-DAY FULL ACCESS</div>

              <ul className="space-y-2 mb-6">
                {[
                  "Full dashboard access",
                  "Live auction monitoring",
                  "AI domain analysis",
                  "Unlimited searches",
                  "Backorder management",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-green-500 text-sm">
                    <span className="text-green-400">[+]</span> {item}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate("/signup")}
                className="w-full px-4 py-3 border-2 border-green-700 text-green-500 hover:border-green-500 hover:bg-green-950/30 transition-all tracking-wider font-bold text-sm"
              >
                [START TRIAL]
              </button>

              <div className="mt-4 text-green-700 text-xs text-center">
                Trial applies to subscription if upgraded
              </div>
            </div>

            {/* Standard Package */}
            <div
              className="border-2 border-green-700 bg-black p-6"
              style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.15)" }}
            >
              <div className="text-green-600 text-xs tracking-wider mb-2">[STANDARD]</div>
              <div className="text-4xl font-bold text-green-400 mb-1">$49.99</div>
              <div className="text-green-600 text-sm mb-6">PER MONTH</div>

              <ul className="space-y-2 mb-6">
                {[
                  "Everything in trial",
                  "Smart automation rules",
                  "Bidding tools",
                  "Watchlist alerts",
                  "Email notifications",
                  "Portfolio analytics",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-green-500 text-sm">
                    <span className="text-green-400">[+]</span> {item}
                  </li>
                ))}
              </ul>

              <div className="border border-yellow-700 bg-yellow-950/20 p-3 mb-4">
                <div className="text-yellow-500 text-xs font-bold mb-1">USAGE FEES</div>
                <div className="text-yellow-600 text-[10px]">
                  $1 per automation trigger, bid action, or watchlist alert
                </div>
              </div>

              <button
                onClick={() => navigate("/signup")}
                className="w-full px-4 py-3 border-2 border-green-500 text-green-500 hover:bg-green-500 hover:text-black transition-all tracking-wider font-bold text-sm"
              >
                [GET STANDARD]
              </button>

              <div className="mt-4 text-green-700 text-xs text-center">
                Cancel anytime
              </div>
            </div>

            {/* Pro Package */}
            <div
              className="border-2 border-green-500 bg-black p-6 relative"
              style={{ boxShadow: "0 0 30px rgba(34, 197, 94, 0.3)" }}
            >
              <div className="absolute -top-3 right-4 bg-green-500 text-black px-3 py-1 text-xs font-bold tracking-wider">
                BEST VALUE
              </div>
              <div className="text-green-500 text-xs tracking-wider mb-2">[PRO]</div>
              <div className="text-4xl font-bold text-green-400 mb-1">$99.99</div>
              <div className="text-green-500 text-sm mb-6">PER MONTH</div>

              <ul className="space-y-2 mb-6">
                {[
                  "Everything in Standard",
                  "UNLIMITED automations",
                  "UNLIMITED bids",
                  "UNLIMITED watchlist",
                  "Priority support",
                  "WHOIS lookup tool",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-green-500 text-sm">
                    <span className="text-green-400">[+]</span> {item}
                  </li>
                ))}
              </ul>

              <div className="border border-green-500 bg-green-950/30 p-3 mb-4">
                <div className="text-green-400 text-xs font-bold mb-1">NO USAGE FEES</div>
                <div className="text-green-600 text-[10px]">
                  Unlimited automations, bids, and alerts included
                </div>
              </div>

              <button
                onClick={() => navigate("/signup")}
                className="w-full px-4 py-3 bg-green-500 text-black font-bold hover:bg-green-400 transition-all tracking-wider text-sm"
                style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.5)" }}
              >
                [GET PRO ACCESS]
              </button>

              <div className="mt-4 text-green-600 text-xs text-center">
                Best for active traders
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About DropCatch Section */}
      <section className="py-20 px-6 border-y-2 border-green-700 bg-green-950/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-green-600 text-sm mb-2 tracking-widest">&gt; SYSTEM INTEL</div>
            <h2 className="text-3xl md:text-4xl font-bold text-green-400 tracking-wider">
              ABOUT DROPCATCH
            </h2>
          </div>

          <div className="border-2 border-green-700 bg-black p-8 mb-8">
            <p className="text-green-500 leading-relaxed mb-6">
              DropCatch.com is an established online service facilitating the backordering of
              expiring domain names. Owned by TurnCommerce, the parent company of NameBright and
              HugeDomains, DropCatch utilizes a network of ICANN-accredited registrars, estimated
              to be approximately 43% of the total market coverage.
            </p>
            <p className="text-green-600 leading-relaxed">
              The company operates an open auction system for domain sales, with payment options
              via credit card or linked NameBright accounts. Zero Point Domains provides a
              professional interface to access and manage your DropCatch activities with enhanced
              tools and automation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: "⬡", title: "ICANN ACCREDITED", desc: "Backed by official registrar network" },
              { icon: "◈", title: "ESTABLISHED 2010", desc: "Over a decade of proven service" },
              { icon: "◉", title: "SECURE PAYMENTS", desc: "Credit card & NameBright integration" },
            ].map((item, i) => (
              <div key={i} className="border border-green-700 p-4 text-center">
                <div className="text-2xl text-green-500 mb-2">{item.icon}</div>
                <h3 className="text-green-400 font-bold tracking-wider text-sm mb-1">
                  {item.title}
                </h3>
                <p className="text-green-600 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="border-2 border-green-500 bg-black p-12"
            style={{ boxShadow: "0 0 40px rgba(34, 197, 94, 0.3)" }}
          >
            <pre className="text-green-500 text-xs mb-6 hidden md:block">
              {`  _____ _____ _____ _____ _____
 |   __|_   _|  _  | __  |_   _|
 |__   | | | |     |    -| | |
 |_____| |_| |__|__|__|__| |_|  `}
            </pre>
            <h2 className="text-2xl md:text-3xl font-bold text-green-400 tracking-wider mb-4">
              READY TO DOMINATE THE
              <br />
              DOMAIN AFTERMARKET?
            </h2>
            <p className="text-green-600 mb-8 max-w-lg mx-auto">
              Join professional domain investors using Zero Point Domains to gain an edge in the
              DropCatch marketplace.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigate("/signup")}
                className="px-8 py-4 bg-green-500 text-black font-bold hover:bg-green-400 transition-all tracking-wider"
                style={{ boxShadow: "0 0 30px rgba(34, 197, 94, 0.5)" }}
              >
                [CREATE ACCOUNT]
              </button>
              <button
                onClick={() => navigate("/login")}
                className="px-8 py-4 border-2 border-green-500 text-green-500 hover:bg-green-950/30 transition-all tracking-wider"
              >
                [LOGIN]
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-green-500 bg-black">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <LogoCircuit size="sm" animate={false} showText={true} />
              </div>
              <p className="text-green-700 text-xs leading-relaxed">
                Professional domain acquisition tools for serious investors.
              </p>
            </div>
            <div>
              <h4 className="text-green-400 font-bold tracking-wider text-sm mb-4">PRODUCT</h4>
              <ul className="space-y-2 text-green-600 text-xs">
                <li className="hover:text-green-400 cursor-pointer">Features</li>
                <li className="hover:text-green-400 cursor-pointer">Pricing</li>
                <li className="hover:text-green-400 cursor-pointer">API Access</li>
                <li className="hover:text-green-400 cursor-pointer">Documentation</li>
              </ul>
            </div>
            <div>
              <h4 className="text-green-400 font-bold tracking-wider text-sm mb-4">COMPANY</h4>
              <ul className="space-y-2 text-green-600 text-xs">
                <li className="hover:text-green-400 cursor-pointer">About</li>
                <li className="hover:text-green-400 cursor-pointer">Contact</li>
                <li className="hover:text-green-400 cursor-pointer">Support</li>
              </ul>
            </div>
            <div>
              <h4 className="text-green-400 font-bold tracking-wider text-sm mb-4">LEGAL</h4>
              <ul className="space-y-2 text-green-600 text-xs">
                <li className="hover:text-green-400 cursor-pointer">Privacy Policy</li>
                <li className="hover:text-green-400 cursor-pointer">Terms of Service</li>
                <li className="hover:text-green-400 cursor-pointer">Cookie Policy</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-green-900 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-green-700 text-xs">
              (C) 2025 THE ZERO ENERGY GROUP. ALL RIGHTS RESERVED.
            </div>
            <div className="flex items-center gap-2 text-green-600 text-xs">
              <span>STATUS:</span>
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-green-500">ONLINE</span>
              <span className="animate-pulse ml-2">*** READY ***</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
